import classnames from 'classnames';
import React, { useState } from 'react';
import { Button, Modal, Tab, Tabs, Spinner } from 'react-bootstrap';
import { connect } from 'react-redux';
import avatarProvider from 'v2//images/avatarProvider.png';
import BackgroundImages, {
  BG_IMAGES,
} from 'v2/common/Background/BackgroundImages';
import InfiniteScrollV2 from 'v2/common/InfiniteScrollV2/InfiniteScrollV2';
import TabNosReults from 'v2/common/TabNosReults/TabNosReults';
import { GenericObject, ProposalEntity, SearchEntity } from 'v2/models';
import { useInfiniteArtificialProposalResponses } from 'v2/queries/artificial-proposal-responses';
import { ResponsesList } from './ResponsesList';
import { ResponsesListItem } from './ResponsesList/types';
import { getResponsesListItems } from './ResponsesList/utils';
import { SearchResponseItemProps } from './SearchResponseItem';
import styles from './searchResponses.module.scss';

export enum SearchStatus {
  New = 'new',
  Accepted = 'accepted',
  Rejected = 'rejected',
  Pending = 'pending',
}

export enum SearchTab {
  All = 'all',
  New = 'new',
  Accepted = 'accepted',
  Rejected = 'rejected',
  Pending = 'pending',
}

export const SEARCH_STATUS_TO_TAB: Record<SearchStatus, SearchTab> = {
  [SearchStatus.New]: SearchTab.New,
  [SearchStatus.Pending]: SearchTab.Pending,
  [SearchStatus.Rejected]: SearchTab.Rejected,
  [SearchStatus.Accepted]: SearchTab.Accepted,
};

export type SearchResponsesPerTab = Record<
  SearchTab,
  SearchResponseItemProps[]
>;

interface StateProps {
  searchResponses: SearchResponsesPerTab;
  proposalsLoaded: boolean;
}

type ResponsesOwnProps = {
  search: SearchEntity;
};

type ResponsesProps = ResponsesOwnProps & StateProps;

const ACTIVE_SEARCH_TABS = [
  {
    eventKey: SearchTab.New,
    title: `New`,
  },
  {
    eventKey: SearchTab.Accepted,
    title: `Accepted`,
  },
  {
    eventKey: SearchTab.Rejected,
    title: `Rejected`,
  },
  {
    eventKey: SearchTab.All,
    title: `All Responses`,
  },
];

const ARCHIVED_SEARCH_TAB = [
  {
    eventKey: SearchTab.All,
    title: `All Responses`,
  },
  {
    eventKey: SearchTab.Accepted,
    title: `Accepted`,
  },
  {
    eventKey: SearchTab.Rejected,
    title: `Rejected`,
  },
];

function shouldRenderArtificialResponses(tab: SearchTab): boolean {
  return tab === SearchTab.All || tab === SearchTab.New;
}

const Responses = ({
  searchResponses,
  search,
  proposalsLoaded,
}: ResponsesProps) => {
  const TABS = search.active ? ACTIVE_SEARCH_TABS : ARCHIVED_SEARCH_TAB;
  const defaultTab = getDefaultTab();
  const [key, setKey] = useState<SearchTab>(defaultTab);
  const [showAcceptedModal, setShowAcceptedModal] = useState(false);
  const [lastAcceptedResonse, setLastAcceptedResonse] =
    useState<SearchResponseItemProps>();

  const {
    data: infiniteArtificialResponses,
    hasNextPage: hasArtificialResponsesNextPage,
    fetchNextPage: fetchNextArtificialResponsesPage,
  } = useInfiniteArtificialProposalResponses({
    searchID: search.id,
  });

  const artificialProposalResponses =
    infiniteArtificialResponses?.pages.flatMap((page) => page.results) ?? [];
  const artificialResponsesCount =
    infiniteArtificialResponses?.pages.at(0)?.count ?? 0;

  if (!proposalsLoaded) {
    return (
      <div className="loadingIndicatorContainer">
        <Spinner animation="border" />
      </div>
    );
  }

  const renderAcceptedProposalModal = () => {
    const hideModal = () => {
      setShowAcceptedModal(false);
      setLastAcceptedResonse(undefined);
    };

    return (
      <Modal
        className={styles.acceptedResponseModal}
        size="sm"
        centered
        show={showAcceptedModal}
        onHide={hideModal}
        scrollable
      >
        <Modal.Header className="bg-light d-flex align-items-center">
          <Modal.Title>Response Accepted</Modal.Title>
          <Button variant="link" onClick={hideModal}>
            Close
          </Button>
        </Modal.Header>
        <Modal.Body>
          <p className="pb-5 pt-3 text-light4">
            {lastAcceptedResonse?.isThirdPartyResponse ? (
              <span>
                This response has been saved to your accepted responses. This
                data seller only sells directly through their website so please
                visit it to learn more.
              </span>
            ) : (
              <span>
                A conversation has been opened between you and the provider. You
                can go directly to the conversation now or wait for them to
                respond first.
              </span>
            )}
          </p>
        </Modal.Body>
      </Modal>
    );
  };

  const renderBgImage = () => (
    <BackgroundImages
      showTopImage={false}
      bottomImage={BG_IMAGES.bottomRightYellowBgImage}
    />
  );

  const renderNoResponsesMessage = (message: string, showTopImage: boolean) => {
    return <TabNosReults message={message} showTopImage={showTopImage} />;
  };

  function getDefaultTab() {
    const newResponses = searchResponses[SearchTab.New]?.length || 0;
    let defaultTab = SearchTab.New;
    if (!search.active || (search.active && !newResponses)) {
      defaultTab = SearchTab.All;
    }
    return defaultTab;
  }

  const listItemsPerTab: Record<SearchTab, ResponsesListItem[]> = {
    [SearchTab.All]: [],
    [SearchTab.Pending]: [],
    [SearchTab.New]: [],
    [SearchTab.Rejected]: [],
    [SearchTab.Accepted]: [],
  };
  Object.values(SearchTab).forEach((searchTab) => {
    listItemsPerTab[searchTab] = getResponsesListItems({
      tab: searchTab,
      searchResponses,
      artificialProposalResponses,
    });
  });
  const listItems = listItemsPerTab[key];

  const rootClasses = classnames('container-xl', 'fix-bg-images', styles.root, {
    'h-100': !listItems.length,
  });

  if (
    listItemsPerTab[SearchTab.All].length === 0 &&
    proposalsLoaded &&
    artificialResponsesCount === 0
  ) {
    return (
      <div className={classnames(rootClasses, 'h-100', styles.noResponseTab)}>
        {renderNoResponsesMessage(
          `There are no responses${search.active ? ' yet!' : ''}`,
          true
        )}
      </div>
    );
  }

  return (
    <>
      <div className={classnames(rootClasses, 'min-h-100 mt-4')}>
        {renderBgImage()}
        <Tabs
          className={classnames('border-bottom-0')}
          activeKey={key}
          onSelect={(key) => setKey((key as SearchTab) || defaultTab)}
          role="tab"
          data-cy="responses:tabs"
        >
          {TABS.map((item) => (
            <Tab
              key={item.eventKey}
              className="border-primary"
              eventKey={item.eventKey}
              unmountOnExit={true}
              title={`${item.title} (${listItemsPerTab[item.eventKey].length})`}
            >
              <div
                className={classnames('pt-3 h-100 position-relative', {
                  [styles.noResponseTab]:
                    !listItemsPerTab[item.eventKey].length,
                })}
              >
                {key !== 'all' &&
                !listItemsPerTab[item.eventKey].length &&
                proposalsLoaded
                  ? renderNoResponsesMessage(
                      `There are no ${item.title.toLowerCase()} responses${
                        search.active ? ' yet!' : ''
                      } `,
                      false
                    )
                  : null}

                <ResponsesList
                  responsesListItems={listItems}
                  onAccepted={(response) => {
                    setShowAcceptedModal(true);
                    setLastAcceptedResonse(response);
                  }}
                  request={search}
                />

                <InfiniteScrollV2
                  fetchNextPage={fetchNextArtificialResponsesPage}
                  hasNextPage={hasArtificialResponsesNextPage}
                  disabled={!shouldRenderArtificialResponses(key)}
                />
              </div>
            </Tab>
          ))}
        </Tabs>
        {renderAcceptedProposalModal()}
      </div>
    </>
  );
};

const mapStateToProps = (state: GenericObject, ownProps: ResponsesOwnProps) => {
  const search: SearchEntity = ownProps.search;
  const proposalsLoaded = Boolean(state.proposals.loaded);
  const proposals: ProposalEntity[] = state.proposals.multipleProposals.filter(
    (item: ProposalEntity) =>
      item.search === search.id && item.archived === !search.active
  );
  const searchResponses: SearchResponsesPerTab = {
    [SearchTab.All]: [],
    [SearchTab.Pending]: [],
    [SearchTab.New]: [],
    [SearchTab.Rejected]: [],
    [SearchTab.Accepted]: [],
  };

  if (!proposals.length) {
    return {
      searchResponses,
      proposalsLoaded,
    };
  }

  proposals.forEach((proposal) => {
    const status: SearchStatus = getSearchStatus(proposal);
    const searchResponse = buildSearchResponse(status, proposal, search.active);
    searchResponses[status].push(searchResponse);
    searchResponses[SearchTab.All].push(searchResponse);
  });

  return { searchResponses, proposalsLoaded };
};

export default connect<StateProps, null, ResponsesOwnProps>(mapStateToProps)(
  Responses
);

export function getSearchStatus(
  proposal: ProposalEntity,
  pendingStatus = false
): SearchStatus {
  if (!proposal.data_buyer_response) {
    if (pendingStatus) {
      return SearchStatus.Pending;
    }
    return SearchStatus.New;
  }
  if (proposal.data_buyer_response.action === 'affirmative') {
    return SearchStatus.Accepted;
  }
  if (proposal.data_buyer_response.action === 'negative') {
    return SearchStatus.Rejected;
  }

  throw Error('Invalid proposal status');
}

export function buildSearchResponse(
  status: SearchStatus,
  proposal: ProposalEntity,
  active: boolean
): SearchResponseItemProps {
  const getProvider = () => {
    return {
      id: proposal.data_provider.id,
      name: proposal.data_provider.name || '',
      vague_description: proposal.data_provider.vague_description || '',
      short_description: proposal.data_provider.short_description || '',
      long_description: proposal.data_provider.long_description || '',
      logo:
        proposal.data_provider?.avatar?.image?.formats?.thumbnail.url ||
        proposal.data_provider?.avatar?.image?.url ||
        avatarProvider,
      external_links: proposal.data_provider?.external_links,
    };
  };

  return {
    status,
    proposalDetail: {
      proposalId: proposal.id,
      response: proposal.data_provider_response?.response,
      dataFiles: proposal.data_provider_response?.data_files,
      answers: proposal.data_provider_response?.answers.map((item) => ({
        question: item.question.name,
        response: item.response,
      })),
      submittedBy: proposal.data_provider_response?.submitted_by,
      dataProvider: getProvider(),
      createdAt: proposal.created_at,
    },
    active,
    isThirdPartyResponse: proposal.third_party_response,
    hideIdentity: proposal.data_provider_response?.hide_identity || false,
  };
}
